import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  createItem,
  getNotificationSettings,
  uploadItemImage,
} from "../api/client";
import { ErrorBanner, ScreenHeader } from "../components/AppSurface";
import { ItemForm } from "../components/ItemForm";
import { TemplateGrid } from "../components/TemplateGrid";
import { useGroup } from "../contexts/GroupContext";
import { useItemsCache } from "../contexts/ItemsCacheContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import { trackItemCreate } from "../lib/analytics";
import type { ItemTemplate } from "../lib/templates";

export function ItemNew() {
  const { t } = useTranslation(["items", "templates"]);
  const navigate = useNavigate();
  const { addItem } = useItemsCache();
  const { isPro } = useSubscription();
  const { groups, activeGroupId } = useGroup();
  const [selectedTemplate, setSelectedTemplate] = useState<ItemTemplate | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preferredHours, setPreferredHours] = useState<number[]>();
  const [hoursLoading, setHoursLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getNotificationSettings()
      .then((s) => setPreferredHours(s.preferredHours))
      .catch(() => {})
      .finally(() => setHoursLoading(false));
  }, []);

  useEffect(() => {
    if (groups.length === 0) navigate("/groups/new", { replace: true });
  }, [groups.length, navigate]);

  function handleSelectTemplate(template: ItemTemplate) {
    setSelectedTemplate(template);
    setShowForm(true);
  }

  function handleCustom() {
    setSelectedTemplate(null);
    setShowForm(true);
  }

  function getFutureDateString(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  async function handleSubmit(data: {
    name: string;
    intervalDays: number;
    nextDueAt: string;
    notificationOffsets: string;
    memo: string;
    imageFile?: File;
  }) {
    if (!activeGroupId) return;
    setLoading(true);
    setError(null);
    try {
      let created = await createItem(activeGroupId, {
        name: data.name,
        intervalDays: data.intervalDays,
        nextDueAt: data.nextDueAt,
        notificationOffsets: data.notificationOffsets
          .split(",")
          .map(Number)
          .filter((n) => !Number.isNaN(n)),
        memo: data.memo || undefined,
        iconUrl: selectedTemplate?.iconUrl,
      });
      let imageUploadFailed = false;
      if (data.imageFile) {
        try {
          created = await uploadItemImage(
            activeGroupId,
            created.id,
            data.imageFile,
          );
        } catch {
          imageUploadFailed = true;
        }
      }
      addItem({ item: created, purchaseHistory: [] });
      trackItemCreate({
        item_name: data.name,
        interval_days: data.intervalDays,
        from_template: selectedTemplate !== null,
      });
      navigate("/home", {
        replace: true,
        state: {
          successMessage: imageUploadFailed
            ? t("items:new.successImageFailed", { name: data.name })
            : t("items:new.success", {
                name: data.name,
                defaultValue: `「${data.name}」を追加しました`,
              }),
        },
      });
    } catch {
      setError(t("items:errors.save"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh bg-white">
      <ScreenHeader
        title={
          showForm ? t("items:new.addItem") : t("items:new.selectTemplate")
        }
        onBack={() => (showForm ? setShowForm(false) : navigate(-1))}
      />

      <main className="mx-auto max-w-2xl px-4 py-5 sm:px-6">
        {error && (
          <div className="mb-4">
            <ErrorBanner>{error}</ErrorBanner>
          </div>
        )}
        {showForm ? (
          <ItemForm
            initialData={
              selectedTemplate
                ? {
                    name: t(selectedTemplate.nameKey),
                    intervalDays: selectedTemplate.intervalDays,
                    nextDueAt: getFutureDateString(
                      selectedTemplate.intervalDays,
                    ),
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            submitLabel={t("common:buttons.add")}
            loading={loading}
            preferredHours={preferredHours}
            preferredHoursLoading={hoursLoading}
            imageEditing={{
              iconUrl: selectedTemplate?.iconUrl,
              isPro,
            }}
          />
        ) : (
          <div>
            <div>
              <button
                type="button"
                onClick={handleCustom}
                className="flex h-[52px] w-full items-center justify-center rounded-[14px] border border-[#e3e8f0] bg-white text-sm font-bold text-gray-700 transition-colors hover:border-blue-300 hover:bg-[#edf3ff] hover:text-[#285bbf]"
              >
                {t("items:new.customAdd")}
              </button>
            </div>
            <div className="mt-7">
              <TemplateGrid onSelect={handleSelectTemplate} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
